import { useEffect, useState } from "react";
import { Star, MessageSquare } from "lucide-react";

import { apiGet } from "@/services/api";

type Review = {
  id: string;
  rating: number;
  title: string;
  comment: string;
  createdAt: string;
  user: { name: string | null } | null;
  product: { title: string } | null;
};

export function VendorReviewsPage() {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void apiGet<{ reviews: Review[] }>("/api/vendors/me/reviews")
      .then((d) => setReviews(d?.reviews ?? []))
      .finally(() => setLoading(false));
  }, []);

  const avgRating =
    reviews.length > 0
      ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
      : "5.0";

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white">
            Customer Feedback & Reviews
          </h1>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            Verified ratings and reviews left by buyers on your products.
          </p>
        </div>

        {reviews.length > 0 && (
          <div className="inline-flex items-center gap-2 rounded-xl border border-amber-300/80 bg-amber-50/70 px-3 py-1.5 dark:border-amber-900/40 dark:bg-amber-950/40 self-start sm:self-center">
            <Star className="h-4 w-4 fill-amber-400 text-amber-500" />
            <span className="text-xs font-black text-amber-950 dark:text-amber-200">
              {avgRating} / 5.0
            </span>
            <span className="text-xs text-slate-400">({reviews.length} reviews)</span>
          </div>
        )}
      </div>

      <div className="amazon-card overflow-hidden divide-y divide-slate-100 dark:divide-white/5">
        {loading && reviews.length === 0 ? (
          <p className="p-8 text-center text-xs text-slate-500">Loading reviews…</p>
        ) : reviews.length === 0 ? (
          <div className="p-8 sm:p-12 text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-500 mb-2">
              <MessageSquare className="h-6 w-6" />
            </div>
            <h3 className="font-bold text-sm text-slate-900 dark:text-white">No reviews yet</h3>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400 max-w-xs mx-auto">
              Reviews from verified buyers who purchase your products will appear here.
            </p>
          </div>
        ) : (
          reviews.map((r) => (
            <div key={r.id} className="p-4 sm:p-5 space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-1.5">
                <div className="flex items-center gap-1">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star
                      key={i}
                      className={`h-3.5 w-3.5 ${
                        i < r.rating
                          ? "fill-amber-400 text-amber-500"
                          : "text-slate-200 dark:text-slate-700"
                      }`}
                    />
                  ))}
                  <span className="ml-1.5 text-xs font-black text-slate-900 dark:text-white">
                    {r.title}
                  </span>
                </div>
                <span className="text-[11px] text-slate-400">
                  {new Date(r.createdAt).toLocaleDateString()}
                </span>
              </div>

              <p className="text-xs sm:text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
                {r.comment}
              </p>

              <div className="flex flex-wrap items-center gap-2 pt-1 text-[11px] text-slate-500 dark:text-slate-400">
                <span className="font-bold text-slate-700 dark:text-slate-200">
                  {r.user?.name ?? "Verified Buyer"}
                </span>
                <span>·</span>
                <span className="text-slate-500 truncate max-w-xs">
                  Purchased: {r.product?.title ?? "Product"}
                </span>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

